import React, { useEffect, useRef, useState } from 'react';
import { Button, FieldMessage, FormField, Overlay, SelectField, Translate, hintStyle } from '../../components/purchasing';
import { getListOrderTemplatesForOrder } from '../../services/ordertemplates.service';
import { createOrders, OrderDetail, PurchaseOrderPayload } from '../../services/order.service';
import { showApiError } from '../../services/apiError';
import { isDueForReorder, qtyToLevel, supplierUnitCost, unitFactor } from './orderLineRules';

interface Option {
  id: string;
  name: string;
}

interface NewOrderDialogProps {
  open: boolean;
  phone: boolean;
  t: Translate;
  language: any;
  sites: Option[];
  suppliers: Option[];
  defaultSiteId: string;
  onClose: () => void;
  onCreated: (id: string) => void;
}

const TEMPLATE_LIMIT = 100;

/** Site, template and supplier; the order starts with the template lines that are due for reorder. */
const NewOrderDialog: React.FC<NewOrderDialogProps> = ({ open, phone, t, language, sites, suppliers, defaultSiteId, onClose, onCreated }) => {
  const [siteId, setSiteId] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [supplierId, setSupplierId] = useState('');
  const [templates, setTemplates] = useState<any[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const requestSeq = useRef(0);

  useEffect(() => {
    if (!open) return;
    setSiteId(defaultSiteId || (sites.length === 1 ? sites[0].id : ''));
    setTemplateId('');
    setSupplierId('');
    setErrors({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open || !siteId) {
      setTemplates([]);
      return;
    }
    const mine = ++requestSeq.current;
    setLoadingTemplates(true);
    getListOrderTemplatesForOrder(1, TEMPLATE_LIMIT, siteId, '')
      .then((response: any) => {
        if (mine !== requestSeq.current) return;
        const rows: any[] = response?.data?.data?.orderTemplates ?? [];
        setTemplates(rows);
        setTemplateId((current) => (rows.some((row) => row.id === current) ? current : rows.length === 1 ? rows[0].id : ''));
      })
      .catch((error: any) => {
        if (mine === requestSeq.current) showApiError(error, { language });
      })
      .finally(() => {
        if (mine === requestSeq.current) setLoadingTemplates(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, siteId]);

  const create = async () => {
    const nextErrors: Record<string, string> = {};
    if (!siteId) nextErrors.site = t('Choose a site');
    if (!templateId) nextErrors.template = t('Choose a template');
    if (!supplierId) nextErrors.supplier = t('Choose a supplier');
    const template = templates.find((row) => row.id === templateId);
    // Each template line due for reorder is its own order line in its unit; Reorder at counts in that unit.
    // It orders what brings stock up to the usual level; a line that needs nothing is left off.
    const orderDetails: OrderDetail[] = (template?.details ?? [])
      .filter((v: any) => isDueForReorder(v?.product?.remainingQtySum, v?.lowQty, v?.product?.productUOMs, v?.productUOMId))
      .map((v: any): { v: any; orderQty: number } => ({ v, orderQty: qtyToLevel(v?.medQty, v?.product?.remainingQtySum, v?.product?.productUOMs, v?.productUOMId) }))
      .filter(({ orderQty }: { orderQty: number }) => orderQty > 0)
      .map(({ v, orderQty }: { v: any; orderQty: number }) => {
        const uomSelect = v.product.productUOMs.find((val: any) => (v?.productUOMId ? v.productUOMId === val?.id : val.isBase));
        const factor = unitFactor(v.product.productUOMs, uomSelect?.id);
        const cost = supplierUnitCost(v.product.supplierInventory, supplierId, uomSelect?.id, factor) ?? 0;
        return {
          productId: v.productId,
          orderQty,
          deliveryQty: 0,
          varianceQty: 0,
          cost,
          lineCost: cost * orderQty,
          taxId: v?.product?.productPrices ? v.product.productPrices[0]?.tax?.id : '',
          productUOMId: v?.productUOMId ?? '',
          conversionFactorUsed: Number(uomSelect?.conversionFactor) ?? 0,
        } as OrderDetail;
      });
    const noUnit = orderDetails.find((line: any) => !line.productUOMId);
    if (noUnit) {
      const name = (template?.details ?? []).find((v: any) => v.productId === noUnit.productId)?.product?.productName ?? '';
      nextErrors.template = t('{product} has no order unit on this template. Set one in Order templates, then try again.', { product: name });
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    const payload: PurchaseOrderPayload = {
      clientId: supplierId,
      // Orders keep no comment; the field stays in the payload shape.
      comment: '',
      internalNumber: 0,
      companyId: '',
      locationId: siteId,
      statusId: 1,
      amount: 0,
      delivery: 0,
      tax: 0,
      orderTemplateId: templateId,
      orderDetails,
    };
    setBusy(true);
    try {
      const response: any = await createOrders(payload);
      const id = response?.data?.data?.id;
      if (id) onCreated(id);
      else onClose();
    } catch (error: any) {
      showApiError(error, { language });
    } finally {
      setBusy(false);
    }
  };

  const field = (key: string, label: string, control: React.ReactNode, hint?: React.ReactNode) => (
    <FormField id={`pu-new-${key}`} label={label} error={errors[key]} hint={hint}>
      {control}
    </FormField>
  );

  const footer = (
    <div style={{ display: 'flex', flexDirection: phone ? 'column-reverse' : 'row', justifyContent: 'flex-end', gap: phone ? '8px' : '12px' }}>
      <Button phone={phone} variant={phone ? 'text' : 'secondary'} onClick={onClose}>
        {t('Cancel')}
      </Button>
      <Button phone={phone} variant="primary" fullWidth={phone} disabled={busy} aria-busy={busy || undefined} onClick={create}>
        {busy ? t('Creating order…') : t('Create order')}
      </Button>
    </div>
  );

  return (
    <Overlay open={open} phone={phone} title={t('New order')} titleId="pu-new-order-title" closeLabel={t('Close')} onClose={onClose} footer={footer} width={520}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', paddingTop: '4px' }}>
        {field(
          'site',
          t('Site'),
          <SelectField
            id="pu-new-site"
            phone={phone}
            invalid={!!errors.site}
            aria-invalid={!!errors.site || undefined}
            aria-describedby={errors.site ? 'pu-new-site-error' : undefined}
            value={siteId}
            onChange={(event) => setSiteId(event.target.value)}
          >
            <option value="">{t('Choose a site')}</option>
            {sites.map((site) => (
              <option key={site.id} value={site.id}>
                {site.name}
              </option>
            ))}
          </SelectField>,
        )}
        {field(
          'template',
          t('Template'),
          <SelectField
            id="pu-new-template"
            phone={phone}
            invalid={!!errors.template}
            aria-invalid={!!errors.template || undefined}
            aria-describedby={errors.template ? 'pu-new-template-error' : undefined}
            disabled={!siteId || loadingTemplates}
            value={templateId}
            onChange={(event) => setTemplateId(event.target.value)}
          >
            <option value="">{loadingTemplates ? t('Loading templates…') : t('Choose a template')}</option>
            {templates.map((template) => (
              <option key={template.id} value={template.id}>
                {template.templateName}
              </option>
            ))}
          </SelectField>,
          siteId && !loadingTemplates && templates.length === 0 ? (
            <FieldMessage tone="notice" align="left">
              {t('This site has no active order templates yet.')}
            </FieldMessage>
          ) : (
            <p style={hintStyle}>{t('The order starts with the template products that are at or below Reorder at.')}</p>
          ),
        )}
        {field(
          'supplier',
          t('Supplier'),
          <SelectField
            id="pu-new-supplier"
            phone={phone}
            invalid={!!errors.supplier}
            aria-invalid={!!errors.supplier || undefined}
            aria-describedby={errors.supplier ? 'pu-new-supplier-error' : undefined}
            value={supplierId}
            onChange={(event) => setSupplierId(event.target.value)}
          >
            <option value="">{t('Choose a supplier')}</option>
            {suppliers.map((supplier) => (
              <option key={supplier.id} value={supplier.id}>
                {supplier.name}
              </option>
            ))}
          </SelectField>,
        )}
      </div>
    </Overlay>
  );
};

export default NewOrderDialog;
